import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileDown, Loader2 } from "lucide-react";
import { API, dayLabel, downloadFile } from "@/lib/api";
import { useT, tDay } from "@/lib/i18n";
import { TopBar } from "@/components/fly/Logo";
import { SearchInput, normText } from "@/components/fly/FilterSelect";

const slug = (s) => normText(s).replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export default function StartList() {
  const { t, lang } = useT();
  const media = (m) => (m ? t(`media.${m}`) : "—");
  const [days, setDays] = useState([]);
  const [day, setDay] = useState("");
  const [perfs, setPerfs] = useState(null);
  const [brks, setBrks] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/startlist`, { params: { day: "" } })
      .then((r) => { setDays(r.data.days); setDay(r.data.days[0] || ""); })
      .catch((e) => { if (e?.response?.status === 403) setHidden(true); });
  }, []);

  useEffect(() => {
    if (!day) return;
    setPerfs(null);
    axios.get(`${API}/public/startlist`, { params: { day } }).then((r) => { setPerfs(r.data.performances); setBrks(r.data.breaks || []); });
  }, [day]);

  const shown = useMemo(() => {
    const n = normText(q.trim());
    const list = (perfs || []).map((p, i) => ({ ...p, nr: i + 1 }))
      .filter((p) => !n || normText(p.name).includes(n) || normText(p.school).includes(n) || normText(p.category).includes(n));
    if (n) return list;
    return [...list, ...brks.map((b) => ({ ...b, isBreak: true }))].sort((a, b) => a.order - b.order);
  }, [perfs, brks, q]);

  const pdf = async () => {
    setBusy(true);
    try {
      await downloadFile({ path: "/public/startlist.pdf", params: { day, lang }, filename: `lista-startowa-${slug(day)}.pdf`, auth: false });
    } catch { toast.error(t("sl.pdfError")); }
    finally { setBusy(false); }
  };

  if (hidden) {
    return (
      <>
        <TopBar lang title={t("sl.title")} />
        <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase">{t("sl.title")}</h1>
          <div data-testid="sl-hidden" className="fly-card p-10 mt-8 text-center text-slate-400">{t("sl.hidden")}</div>
        </main>
      </>
    );
  }

  return (
    <>
      <TopBar lang title={t("sl.title")} />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-blue-400">FLY CUP 2026</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase">{t("sl.title")}</h1>
        <div className="flex flex-wrap gap-2 mt-6" data-testid="sl-day-tabs">
          {days.map((d) => (
            <button key={d} data-testid={`sl-day-${slug(d)}`} onClick={() => setDay(d)}
              className={`h-10 px-4 rounded-full text-sm font-semibold ${day === d ? "bg-blue-600 text-white" : "bg-[#1C2541] text-slate-300 hover:text-white"}`}>{dayLabel(tDay(d, lang))}</button>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row gap-3 mt-4">
          <div className="flex-1"><SearchInput testid="sl-search-input" value={q} onChange={setQ} placeholder={t("sl.search")} /></div>
          <button data-testid="sl-pdf-btn" disabled={!day || busy} onClick={pdf} className="h-12 px-5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-40">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}{t("sl.pdf")}
          </button>
        </div>
        <div className="sm:hidden mt-4 grid gap-2" data-testid="sl-mobile-list">
          {perfs && shown.map((p) => p.isBreak ? (
            <div key={p.id} className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200 font-semibold" data-testid={`sl-break-${p.id}`}><span className="font-mono mr-2">{p.time}</span>{t("sl.break")} – {p.label} ({p.minutes} min)</div>
          ) : (
            <div key={p.id} className="fly-card p-3 grid gap-1" data-testid={`sl-card-${p.id}`}>
              <div className="flex items-center gap-2 font-mono text-xs text-slate-400"><span>#{p.nr}</span><span className="text-white">{p.time}</span><span className="ml-auto text-amber-300">{media(p.media)}</span></div>
              <div className="font-semibold">{p.name}</div>
              <div className="text-xs text-blue-200">{p.category} · <span className="text-slate-400">{p.age_group}</span></div>
              {p.school && <div className="text-xs text-slate-400">{p.school}</div>}
            </div>
          ))}
          {perfs && !shown.length && <p className="p-4 text-slate-400">{t("sl.none")}</p>}
        </div>
        <div className="fly-card mt-4 overflow-x-auto min-w-0 hidden sm:block">
          {!perfs ? <p className="p-6 text-slate-400">{t("sl.loading")}</p> : (
            <table className="w-full text-sm min-w-[640px]" data-testid="sl-table">
              <thead><tr className="text-left font-mono text-[10px] uppercase tracking-wider text-slate-400"><th className="p-3 w-12">{t("sl.nr")}</th><th className="w-16">{t("sl.time")}</th><th>{t("res.performer")}</th><th>{t("sl.category")}</th><th>{t("sl.age")}</th><th>{t("sl.club")}</th><th className="pr-3">{t("sl.media")}</th></tr></thead>
              <tbody>
                {shown.map((p) => p.isBreak ? (
                  <tr key={p.id} data-testid={`sl-break-row-${p.id}`} className="border-t border-white/5 bg-amber-500/10 text-amber-200 font-semibold">
                    <td className="p-3" /><td className="font-mono">{p.time}</td><td colSpan={5}>{t("sl.break")} – {p.label} ({p.minutes} min)</td>
                  </tr>
                ) : (
                  <tr key={p.id} data-testid={`sl-row-${p.id}`} className="border-t border-white/5">
                    <td className="p-3 font-mono text-slate-400">{p.nr}</td>
                    <td className="font-mono">{p.time}</td>
                    <td className="font-semibold">{p.name}</td>
                    <td className="text-blue-200">{p.category}</td>
                    <td className="text-slate-400">{p.age_group}</td>
                    <td className="text-slate-400">{p.school}</td>
                    <td className="pr-3 whitespace-nowrap text-amber-300" data-testid={`sl-media-${p.id}`}>{media(p.media)}</td>
                  </tr>
                ))}
                {!shown.length && <tr><td colSpan={7} className="p-6 text-slate-400" data-testid="sl-empty">{t("sl.none")}</td></tr>}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </>
  );
}
